/**
 * The Lines panel's Add menu (W2-07; DESIGN.md §4.10, §5.5 "Create"): Line, Text line, Image line; at the Full
 * level also UI mover (one entry per HUD element), GIF line, Paint line and Helper (invisible parent); then
 * "New N lines...", "RPE-style 24 lines" (Full) and "Duplicate active line". Every entry is a registered command, so
 * the menu shows its shortcut and disabled reason and the palette offers the same.
 */

import { useT } from '../../i18n/useT';
import { Button } from '../../ui/Button';
import { Menu } from '../../ui/Menu';
import type { MenuItem } from '../../ui/types';
import { m } from './lines.messages';
import { UI_ELEMENT_IDS } from './lineModel';
import s from './Lines.module.css';

export function addMenuItems(t: ReturnType<typeof useT>, full: boolean): MenuItem[] {
  const items: MenuItem[] = [
    { kind: 'command', command: 'lines.add', label: t(m.cmdAddLine) },
    { kind: 'command', command: 'lines.addText', label: t(m.cmdAddText) },
    { kind: 'command', command: 'lines.addImage', label: t(m.cmdAddImage) },
  ];
  if (full) {
    items.push(
      {
        kind: 'submenu',
        label: t(m.cmdAddUiMover),
        items: UI_ELEMENT_IDS.map((element): MenuItem => ({
          kind: 'command',
          command: 'lines.addUiMover',
          args: { element },
          label: t(m.uiElementName, { element }),
        })),
      },
      { kind: 'command', command: 'lines.addGif', label: t(m.cmdAddGif) },
      { kind: 'command', command: 'lines.addPaint', label: t(m.cmdAddPaint) },
      { kind: 'command', command: 'lines.addHelper', label: t(m.cmdAddHelper) },
    );
  }
  items.push({ kind: 'separator' }, { kind: 'command', command: 'lines.addMany', label: t(m.cmdAddMany) });
  if (full) items.push({ kind: 'command', command: 'lines.addRpe24', label: t(m.cmdAddRpe24) });
  items.push({ kind: 'separator' }, { kind: 'command', command: 'lines.duplicate', label: t(m.cmdDuplicateActive) });
  return items;
}

export function AddLineMenu(props: { readonly full: boolean }) {
  const t = useT();
  return (
    <Menu
      label={t(m.addMenuLabel)}
      trigger={
        <Button size="sm" variant="ghost" icon="Plus">
          <span className={s.segText}>{t(m.add)}</span>
        </Button>
      }
      items={addMenuItems(t, props.full)}
    />
  );
}
